<script setup>
import { computed, h } from 'vue';
import { provideDropdownTeleport } from 'next/dropdown-menu/base/provider';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import wootConstants from 'dashboard/constants/globals';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useImpersonation } from 'dashboard/composables/useImpersonation';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSection,
  DropdownItem,
} from 'next/dropdown-menu/base';
import Icon from 'next/icon/Icon.vue';
import Button from 'next/button/Button.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';

defineProps({
  pauseRules: { type: Array, default: () => [] },
  activePause: { type: Object, default: null },
});

const emit = defineEmits([
  'startPause',
  'openActivePause',
  'refreshPauseState',
]);

provideDropdownTeleport();

const { t } = useI18n();
const store = useStore();
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const currentAccountId = useMapGetter('getCurrentAccountId');
const currentUserAutoOffline = useMapGetter('getCurrentUserAutoOffline');

const { isImpersonating } = useImpersonation();

const { AVAILABILITY_STATUS_KEYS } = wootConstants;
const statusList = computed(() => {
  return [
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE'),
  ];
});

const statusColors = ['bg-n-teal-9', 'bg-n-amber-9', 'bg-n-slate-9'];

const availabilityStatuses = computed(() => {
  return statusList.value.map((statusLabel, index) => ({
    label: statusLabel,
    value: AVAILABILITY_STATUS_KEYS[index],
    color: statusColors[index],
    icon: h('span', { class: [statusColors[index], 'size-[12px] rounded'] }),
    active: currentUserAvailability.value === AVAILABILITY_STATUS_KEYS[index],
  }));
});

const activeStatus = computed(() => {
  return availabilityStatuses.value.find(status => status.active);
});

const autoOfflineToggle = computed({
  get: () => currentUserAutoOffline.value,
  set: autoOffline => {
    store.dispatch('updateAutoOffline', {
      accountId: currentAccountId.value,
      autoOffline,
    });
  },
});

function changeAvailabilityStatus(availability) {
  if (isImpersonating.value) {
    useAlert(t('PROFILE_SETTINGS.FORM.AVAILABILITY.IMPERSONATING_ERROR'));
    return;
  }
  try {
    store.dispatch('updateAvailability', {
      availability,
      account_id: currentAccountId.value,
    });
  } catch (error) {
    useAlert(t('PROFILE_SETTINGS.FORM.AVAILABILITY.SET_AVAILABILITY_ERROR'));
  }
}

const openAvailabilityMenu = toggle => {
  emit('refreshPauseState');
  toggle();
};

const openActivePauseDialog = () => {
  emit('openActivePause');
};

const startPause = ruleId => {
  emit('startPause', ruleId);
};
</script>

<template>
  <DropdownSection
    class="min-w-0 [&>ul]:min-w-0 [&>ul]:grid-cols-[minmax(0,1fr)] [&>ul]:overflow-visible [&>ul]:!gap-1.5"
  >
    <div class="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-1.5">
      <DropdownItem
        preserve-open
        class="min-w-0 gap-2 [&>button]:min-w-0 [&>button]:gap-2 !rounded-xl !border !border-n-weak !bg-n-alpha-1 !px-3 !py-2.5"
      >
        <div class="flex min-w-0 flex-1 items-center gap-2">
          <span
            class="grid size-8 shrink-0 place-items-center rounded-lg bg-n-brand/10 text-n-brand"
          >
            <Icon icon="i-lucide-radio" class="size-4" />
          </span>
          <div class="min-w-0 flex-1">
            <span class="block text-[11px] font-medium text-n-slate-10">
              {{ $t('SIDEBAR.SET_YOUR_AVAILABILITY') }}
            </span>
            <span class="block truncate text-sm font-semibold text-n-slate-12">
              {{ activeStatus.label }}
            </span>
          </div>
        </div>
        <DropdownContainer class="shrink-0">
          <template #trigger="{ toggle }">
            <Button
              size="sm"
              color="slate"
              variant="faded"
              icon="i-lucide-chevron-down"
              trailing-icon
              @click="openAvailabilityMenu(toggle)"
            >
              <span class="size-2 rounded-full" :class="activeStatus.color" />
            </Button>
          </template>
          <DropdownBody
            class="z-20 w-56 max-w-[calc(100vw-1rem)] [&>ul]:!gap-1 [&>ul]:!bg-n-solid-1 [&>ul]:!backdrop-blur-none [&>ul]:shadow-xl"
          >
            <DropdownItem
              v-for="status in availabilityStatuses"
              :key="status.value"
              :label="status.label"
              :icon="status.icon"
              class="cursor-pointer !py-1.5"
              @click="changeAvailabilityStatus(status.value)"
            />
            <DropdownSection
              v-if="activePause || pauseRules.length"
              :title="$t('SIDEBAR.PAUSES')"
              height="max-h-48"
              class="mt-1 border-t border-n-weak pt-1.5 [&>div:first-child]:mb-1 [&>div:first-child]:mt-0 [&>ul]:!gap-0.5"
            >
              <DropdownItem
                v-if="activePause"
                class="cursor-pointer gap-2 rounded-lg !py-1.5 text-n-amber-11 transition-colors hover:bg-n-alpha-2"
                @click="openActivePauseDialog"
              >
                <Icon
                  icon="i-lucide-circle-play"
                  class="size-4 shrink-0 text-n-amber-11"
                />
                <span
                  class="min-w-0 flex-1 truncate"
                  :title="activePause.rule_name || $t('SIDEBAR.ACTIVE_PAUSE')"
                >
                  {{ $t('SIDEBAR.END_PAUSE') }}
                </span>
              </DropdownItem>
              <template v-else>
                <DropdownItem
                  v-for="rule in pauseRules"
                  :key="`pause-${rule.id}`"
                  class="cursor-pointer gap-2 rounded-lg !py-1.5 transition-colors hover:bg-n-alpha-2"
                  @click="startPause(rule.id)"
                >
                  <Icon
                    :icon="rule.icon || 'i-lucide-coffee'"
                    class="size-4 shrink-0 text-n-slate-11"
                  />
                  <span class="min-w-0 flex-1 truncate" :title="rule.name">
                    {{ rule.name }}
                  </span>
                </DropdownItem>
              </template>
            </DropdownSection>
          </DropdownBody>
        </DropdownContainer>
      </DropdownItem>
      <DropdownItem
        preserve-open
        class="min-w-0 gap-3 [&>button]:min-w-0 [&>button]:gap-2 [&>button]:whitespace-normal !rounded-xl !px-3 !py-2.5 hover:!bg-n-alpha-1"
      >
        <span
          class="grid size-8 shrink-0 place-items-center rounded-lg bg-n-alpha-2 text-n-slate-11"
        >
          <Icon icon="i-lucide-moon-star" class="size-4" />
        </span>
        <div class="min-w-0 flex-1">
          <span
            class="block whitespace-normal break-words text-sm font-medium text-n-slate-12"
          >
            {{ $t('SIDEBAR.SET_AUTO_OFFLINE.TEXT') }}
          </span>
          <span
            class="block whitespace-normal break-words text-[11px] leading-4 text-n-slate-10"
          >
            {{ $t('SIDEBAR.SET_AUTO_OFFLINE.INFO_SHORT') }}
          </span>
        </div>
        <ToggleSwitch v-model="autoOfflineToggle" />
      </DropdownItem>
    </div>
  </DropdownSection>
</template>
